import LuxonUtils from '@date-io/luxon'
import CssBaseline from '@material-ui/core/CssBaseline'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import * as Sentry from '@sentry/browser'
import * as React from 'react'
import ReactDOM from 'react-dom'
import { pdfjs } from 'react-pdf'
import { QueryClient, QueryClientProvider } from 'react-query'
import { DrawerProvider } from 'shared/components/Drawer/DrawerContext'
import { redirectFromLegacyRoute } from 'shared/helpers/redirectFromLegacyRoute'

import { loadHeap } from 'helpers/loadHeap'
import App from './App'
import { GlobalStyles } from './components/GlobalStyles/GlobalStyles'
import { detectEnvironment, isProductionEnv } from './config/config'
import { maybeSetUpBaseDomain, setCsrfHeader } from './helpers/authHelper'

redirectFromLegacyRoute()

if (process.env.NODE_ENV === 'development') {
  const errorsToIgnore = ['Function components cannot be given refs', 'React does not recognize the']
  const error = console.error
  console.error = (...args) => {
    try {
      if (args[0].match(new RegExp(errorsToIgnore.join('|')))) return
      error(...args)
    } catch (err) {
      error(err)
      error(...args)
    }
  }
}

if (isProductionEnv()) {
  const networkErrorRegex =
    /failed with status code 401|<title>Server Error|Network Error|undefined undefined: undefined/i
  Sentry.init({
    dsn: 'https://3d66bc0209d642cb8bfc465ad31237ec@o367979.ingest.sentry.io/6605924',
    release: process.env.REACT_APP_RELEASE_VERSION,
    environment: detectEnvironment().toLowerCase(),
    normalizeDepth: 7,
    ignoreErrors: ['Non-Error promise rejection captured', networkErrorRegex],
    beforeSend(event, hint) {
      if (hint) {
        const error = hint.originalException as Error
        const errorString = error && error.toString ? error.toString() : ''
        if (error?.message?.match(networkErrorRegex) || errorString?.match(networkErrorRegex)) {
          // do not send network noise as sentry alerts
          return null
        }
      }
      if (event.breadcrumbs) {
        for (const crumb of event.breadcrumbs) {
          if (crumb.category === 'exception' && crumb.message === 'Error: Network Error') {
            // most likely an xhr related network blip. Ignore
            return null
          }
        }
      }
      return event
    },
  })

  Sentry.configureScope((scope) => {
    scope.setTag('React.version', React.version)
  })
}

setCsrfHeader()
maybeSetUpBaseDomain()
loadHeap()

// This line is needed to render pdf previews & allow the user to see them on pages
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

const queryClient = new QueryClient()

ReactDOM.render(
  <MuiPickersUtilsProvider utils={LuxonUtils}>
    <QueryClientProvider client={queryClient}>
      <DrawerProvider>
        <CssBaseline />
        <GlobalStyles />
        <App />
        <style>{`.MuiSnackbarContent-message { color: white; } `}</style>
      </DrawerProvider>
    </QueryClientProvider>
  </MuiPickersUtilsProvider>,
  document.getElementById('root')
)
